import ContentContainer from '@/features/shared/ContentContainer';
import { withGTM } from '@/features/shared/HOCs';
import { MuiContainer } from '@/features/shared/material-ui';
import { resolveTheme } from '@/utils/resolvers';
import clsx from 'clsx';
import React, { memo, useMemo } from 'react';
import CardListRow from './CardListRow/CardListRow';
import { onDatalayer } from './ContentList.datalayer';
import { ContentListProps } from './ContentList.interface';
import useStyles from './ContentList.styles';
import StackedCardList from './StackedCardList/StackedCardList';

const validateHeader = ({ content }: Partial<ContentListProps>) => {
  const { listSuperheading: superheading, listTitle: title } = content || {};
  const existHeader = !!superheading || !!title;
  return existHeader;
};

const mapTemplateToComponent = (template: string) => {
  if (/row$/i.test(template)) {
    return CardListRow;
  }

  return StackedCardList;
};

const ContentList = ({
  content,
  presentation,
  contentButtons,
}: Br.PropsWithContentButtons<ContentListProps>) => {
  const {
    template,
    cardColor,
    containerTopSpacing,
    containerBottomSpacing,
    containerTopBackgroundColor,
    containerBottomBackgroundColor,
    containerTopBackgroundImage,
    hasTextShadow,
    backgroundOverlayOpacity,
    textColor,
    columnNumber,
  } = presentation;
  const theme = useMemo(() => resolveTheme('text-color')({ rawVal: textColor }), [textColor]);
  const isRow = useMemo(() => /row$/i.test(template), [template]);

  const topBackgroundColor = useMemo(
    () => (isRow && containerTopBackgroundImage ? 'transparent' : containerTopBackgroundColor),
    [containerTopBackgroundColor, containerTopBackgroundImage, isRow],
  );
  const backgroundImage = useMemo(
    () => isRow && containerTopBackgroundImage,
    [containerTopBackgroundImage, isRow],
  );
  const cardTheme = useMemo(() => resolveTheme('bg-color')({ rawVal: cardColor }), [cardColor]);
  const classes = useStyles({});
  const Variant = useMemo(() => mapTemplateToComponent(template), [template]);
  const isRowVariant = useMemo(() => /row$/i.test(template), [template]);

  const hasHeader = useMemo(() => validateHeader({ content }), [content]);
  const insetTop = useMemo(() => (hasHeader ? 'content-list' : 'hero'), [hasHeader]);
  const hasAddInsetOffsetSpacing = useMemo(
    () => isRowVariant && backgroundImage,
    [backgroundImage, isRowVariant],
  );

  const containerProps = useMemo(
    () => ({
      topBackgroundColor,
      bottomBackgroundColor: containerBottomBackgroundColor,
      topSpacing: containerTopSpacing,
      bottomSpacing: containerBottomSpacing,
      backgroundImage,
      mobileBackgroundImage: backgroundImage,
      offsetTop: hasAddInsetOffsetSpacing,
      insetTop: hasAddInsetOffsetSpacing ? insetTop : undefined,
      overlay: {
        hasTextShadow: backgroundImage && hasTextShadow,
        opacity: backgroundImage && backgroundOverlayOpacity,
        maxWidth: 'small',
      },
    }),
    [
      backgroundImage,
      backgroundOverlayOpacity,
      containerBottomBackgroundColor,
      containerBottomSpacing,
      containerTopSpacing,
      hasAddInsetOffsetSpacing,
      hasTextShadow,
      insetTop,
      topBackgroundColor,
    ],
  );

  const variantProps = {
    theme,
    cardTheme,
    cardColor,
    hasHeader,
    columnNumber,
  };

  const ContentContainerGTM = withGTM({ Component: ContentContainer, onDatalayer });

  return (
    <ContentContainerGTM {...containerProps}>
      <section className={`${classes.wrapper}`}>
        <MuiContainer className={clsx(classes.container, isRow ? 'large' : 'medium')}>
          <Variant content={content} {...variantProps} contentButtons={contentButtons} />
        </MuiContainer>
      </section>
    </ContentContainerGTM>
  );
};

export default memo(ContentList);
